import React, { useEffect, useState } from "react";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import ClientsService from "../../services/ClientsService/ClientsService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import {
  normalizeClientPolicyValues,
  normalizeClientProperties,
  normalizeDate,
} from "../../utils/NormalizationUtil.ts";
import { ClientDataType } from "../../types/client.ts";
import { ClientTypeEnum } from "../../enum/client-type.enum.ts";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import ClientPoliciesDataGrid from "../../components/DataTables/ClientPoliciesDataGrid.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const ClientData = () => {
  const [clientData, setClientData] = useState<ClientDataType>({});
  const [nationalities, setNationalities] = useState<{ [key: string]: string }>(
    {}
  );
  const [districts, setDistricts] = useState<{ [key: string]: string }>({});
  const [municipalities, setMunicipalities] = useState<{
    [key: string]: string;
  }>({});
  const [cities, setCities] = useState<{ [key: string]: string }>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const [clientPolicies, setClientPolicies] = useState<
    { [key: string]: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const { jwt } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesResult, regionsResult] = await Promise.all([
          ClientsService.getAllCountries(jwt),
          ClientsService.getAllAreas(jwt),
        ]);
        setNationalities(countriesResult as { [key: string]: string });
        setDistricts(regionsResult as { [key: string]: string });
      } catch (error) {
        displayAlert(
          "Грешка при зареждане на данни за клиент",
          FadeAlertType.error
        );
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    if (clientData.district) {
      ClientsService.getAllMunicipalities(jwt, clientData.district)
        .then((result) =>
          setMunicipalities(result as { [key: string]: string })
        )
        .catch((error) => {
          setAlert(error.message);
          setAlertType(FadeAlertType.error);
        });
    }
  }, [clientData.district, jwt]);

  useEffect(() => {
    if (clientData.municipality) {
      ClientsService.getAllCities(jwt, clientData.municipality)
        .then((result) => setCities(result as { [key: string]: string }))
        .catch((error) => {
          setAlert(error.message);
          setAlertType(FadeAlertType.error);
        });
    }
  }, [clientData.municipality, jwt]);

  const validateIdentifier = (identifier: string, type: string): boolean => {
    if (
      type === ClientTypeEnum.Individual ||
      type === ClientTypeEnum.Foreigner
    ) {
      return identifier.length === 10;
    } else if (type === ClientTypeEnum.Company) {
      return identifier.length >= 9 && identifier.length <= 15;
    }
    return identifier.length === 10;
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setClientData({ ...clientData, insuranceClientPin: value });
    }
  };

  const handleIdentifierBlur = async () => {
    const { insuranceClientPin, clientType } = clientData;
    if (
      insuranceClientPin &&
      !validateIdentifier(insuranceClientPin, clientType || "")
    ) {
      setValidationError(
        clientType === ClientTypeEnum.Company
          ? "Невалидно ЕИК"
          : "Невалидно ЕГН"
      );
    } else {
      setValidationError(null);
      await getUserDataById();
    }
  };

  const getUserDataById = async (e?: any): Promise<void> => {
    e?.preventDefault();
    if (!clientData.insuranceClientPin) return;
    try {
      const client = await fetchClientData();
      if (isClientDataEmpty(client)) return;
      await fetchAndSetLocationData(client);
      updateClientData(client);
      displayAlert("Данните са успешно заредени", FadeAlertType.success);
    } catch (error) {
      displayAlert("Грешка при зареждането на клиент", FadeAlertType.error);
      throw new Error(error);
    }
  };

  const fetchClientData = async (): Promise<ClientDataType> => {
    const client = await ClientsService.getClient(
      jwt,
      clientData.insuranceClientPin
    );
    return normalizeClientProperties(client as object);
  };

  const fetchClientPolicies = async (): Promise<void> => {
    if (!clientData.insuranceClientPin) return;
    setIsLoading(true);
    try {
      const clientPolicies = await ClientsService.getClientPolicies(
        jwt,
        clientData.insuranceClientPin
      );
      const normalizedPolicies = (clientPolicies as []).map((policy) =>
        normalizeClientPolicyValues(policy)
      );
      const normalizedPoliciesWithDates = normalizedPolicies.map((policy) => ({
        ...policy,
        fromDate: normalizeDate(policy.fromDate),
        toDate: normalizeDate(policy.toDate),
      }));
      setClientPolicies(normalizedPoliciesWithDates);
      if (normalizedPoliciesWithDates.length > 0) {
        displayAlert(
          `Заредени ${normalizedPoliciesWithDates.length} полици`,
          FadeAlertType.success
        );
      }
    } catch (error) {
      displayAlert("Грешка при зареждане на полици", FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isClientDataEmpty = (clientData: ClientDataType): boolean =>
    Object.values(clientData).length === 0;

  const fetchAndSetLocationData = async (
    clientData: ClientDataType
  ): Promise<void> => {
    const [municipalitiesResult, citiesResult] = await Promise.all([
      ClientsService.getAllMunicipalities(jwt, clientData.district),
      ClientsService.getAllCities(jwt, clientData.municipality),
    ]);
    setMunicipalities(municipalitiesResult as { [key: string]: string });
    setCities(citiesResult as { [key: string]: string });
  };

  const updateClientData = (clientData: ClientDataType): void => {
    setClientData({
      ...clientData,
      district: clientData.district?.substring(0, 3) ?? "",
      municipality: clientData.municipality ?? "",
      ekatteCode: clientData.ekatteCode ?? "",
    });
  };

  const displayAlert = (message: string, type: FadeAlertType): void => {
    setAlert(message);
    setAlertType(type);
  };

  const saveUser = async (e: any): Promise<void> => {
    e.preventDefault();
    if (validationError) {
      displayAlert(
        "Моля, оправете грешно-въведените данни и опитайте отново!",
        FadeAlertType.warning
      );
      return;
    }
    try {
      const normalizedClientData = normalizeClientProperties(clientData, true);
      await ClientsService.saveClient(jwt, normalizedClientData);
      displayAlert("Успешен запис", FadeAlertType.success);
      setClientPolicies([]);
      setClientData({});
    } catch (error: any) {
      displayAlert("Грешка при запис", FadeAlertType.error);
    }
  };

  const determineInsuranceClientIdentifierLabel = (): string => {
    switch (clientData.clientType) {
      case ClientTypeEnum.Foreigner:
        clientData.country = "";
        return "ЛНЧ";
      case ClientTypeEnum.Company:
        return "ЕИК";
      default:
        clientData.clientType = ClientTypeEnum.Individual;
        clientData.country = "BG";
        return "ЕГН";
    }
  };

  const determineInsuranceClientTypeLabel = (): string =>
    clientData.clientType === ClientTypeEnum.Company
      ? "Име на компания"
      : "Име на клиент";

  return (
    <>
      <Breadcrumb pageName="Данни за клиент" />

      <FadeAlert
        alert={alert}
        type={alertType}
        onClose={() => setAlert(null)}
      />

      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <SectionHeader title="Данни за клиент" />

        <div className="mt-2 p-3 pt-1">
          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>
                {determineInsuranceClientIdentifierLabel()}
              </label>
              <input
                type="text"
                value={clientData.insuranceClientPin || ""}
                onChange={handleIdentifierChange}
                onBlur={handleIdentifierBlur}
                className={inputClass}
              />
              {validationError && (
                <p className="mt-1 text-xs text-rose-600">{validationError}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                {determineInsuranceClientTypeLabel()}
              </label>
              <input
                type="text"
                value={clientData.insuranceClientName || ""}
                onChange={(e) =>
                  setClientData({
                    ...clientData,
                    insuranceClientName: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Тип клиент</label>
              <SelectGroup
                options={{
                  1: "Физическо лице",
                  2: "Юридическо лице",
                  3: "Чужденец",
                }}
                value={clientData.clientType || ""}
                onChange={(value) =>
                  setClientData({ ...clientData, clientType: value })
                }
              />
            </div>
            <div>
              <label className={labelClass}>Националност</label>
              <SelectGroup
                options={nationalities}
                value={clientData.country}
                onChange={(value) =>
                  setClientData({ ...clientData, country: value })
                }
                placeholder="Националност"
              />
            </div>
          </div>

          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Област</label>
              <SelectGroup
                options={districts}
                value={clientData.district}
                onChange={(value) =>
                  setClientData({ ...clientData, district: value })
                }
                placeholder="Изберете Област"
              />
            </div>
            <div>
              <label className={labelClass}>Община</label>
              <SelectGroup
                options={municipalities}
                value={clientData.municipality || ""}
                onChange={(value) =>
                  setClientData({ ...clientData, municipality: value })
                }
                placeholder="Изберете Община"
              />
            </div>
            <div>
              <label className={labelClass}>Населено място</label>
              <SelectGroup
                options={cities}
                value={clientData.ekatteCode || ""}
                onChange={(value) =>
                  setClientData({ ...clientData, ekatteCode: value })
                }
                placeholder="Изберете Населено място"
              />
            </div>
            <div>
              <label className={labelClass}>ПК</label>
              <input
                type="text"
                value={clientData.postalCode || ""}
                onChange={(e) =>
                  setClientData({ ...clientData, postalCode: e.target.value })
                }
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div className="col-span-2">
              <label className={labelClass}>Адрес</label>
              <input
                data-no-decimal-guard=""
                type="text"
                value={clientData.insuranceClientAddress || ""}
                onChange={(e) =>
                  setClientData({
                    ...clientData,
                    insuranceClientAddress: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Телефон</label>
              <input
                type="text"
                value={clientData.insuranceClientPhone || ""}
                onChange={(e) =>
                  setClientData({
                    ...clientData,
                    insuranceClientPhone: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-2">
            <label className={labelClass}>Други данни</label>
            <textarea
              data-no-decimal-guard=""
              value={clientData.otherData || ""}
              onChange={(e) =>
                setClientData({ ...clientData, otherData: e.target.value })
              }
              className={inputClass + " resize-none"}
              rows={2}
            />
          </div>

          <div className="mt-3 flex justify-center gap-2">
            <button
              onClick={saveUser}
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Запис
            </button>
            <button
              onClick={fetchClientPolicies}
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Търси полици
            </button>
            <button
              className="bg-accent hover:bg-accent-light flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white transition-colors duration-200 disabled:opacity-50"
              disabled={validationError !== null}
            >
              Изтрий
            </button>
          </div>
        </div>
      </fieldset>

      <ClientPoliciesDataGrid
        clientPoliciesList={clientPolicies}
        isLoading={isLoading}
      />
    </>
  );
};

export default ClientData;
